import {
  Alert,
  Button,
  Dialog,
  Slide,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { forwardRef, useState } from "react";
import CustomDataGrid from "../CustomComponents/CustomDataGrid";
import {
  amortDateConvertor,
  amountFormatter,
  convertEmiToNumber,
  processChangeHeaderModel,
} from "./ReSchedulementHelper";
import { DownloadRounded } from "@mui/icons-material";
import axios from "axios";
import { useRef } from "react";
import { CommonShortCutAction } from "../Store/CommonShortCutsReducer";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

const Transition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => {
  const appbarHeight = 70;
  return {
    root: { top: `${appbarHeight}px !important`, color: "blue" },
  };
});

const RevisedRepaymentDialog = (props) => {
  const classes = useStyles();

  //Snackbar Handler
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [snackBarState, setSnackBarState] = useState({
    vertical: "bottom",
    horizontal: "left",
  });
  const { vertical, horizontal } = snackBarState;
  const [alert, setAlert] = useState(false);

  const deviceModeValue = useMediaQuery("(max-width:1200px)");
  useEffect(() => {
    // true -> mobile or tab, false -> desktop
    let SnackPosition = {
      vertical: deviceModeValue ? "top" : "bottom",
      horizontal: deviceModeValue ? "right" : "left",
    };
    setSnackBarState(SnackPosition);
  }, []);

  const handleClose = () => {
    props.closeDialog(false);
  };
  const dispatch = useDispatch();
  //Escape Key Implementation
  const downloadRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    window.addEventListener("keydown", onKeyUnmountEventHandler);
    window.addEventListener("keyup", onKeyPressEventHandler);
    return () => {
      window.addEventListener("keydown", onKeyUnmountEventHandler);
      window.addEventListener("keyup", onKeyPressEventHandler);
    };
  }, []);
  const onKeyUnmountEventHandler = (event) => {
    if (event.ctrlKey) {
      dispatch(
        CommonShortCutAction.removeDefaultShortCutValue({
          event: event,
          screen: "reSchedulement",
        })
      );
    }
  };
  const onKeyPressEventHandler = (event) => {
    let refList = { downloadRef: downloadRef, closeRef: closeRef };
    if (event.ctrlKey) {
      dispatch(
        CommonShortCutAction.getShortCutValue({
          event: event,
          refs: refList,
          screen: "reSchedulement",
        })
      );
    }
  };

  const onDownload = async () => {
    await axios
      .post(
        "/generateReSchedulementReport",
        {
          applicationNum: props.filterValues.applicationNum,
          currentEmi: props.filterValues.currentEmi,
          currentTenure: props.filterValues.currentTenure,
          revisedTenure: props.filterValues.revisedTenure,
          revisedEmi: convertEmiToNumber(props.filterValues.revisedEmi),
          processHeader: props.viewAmortBeforeSave
            ? processChangeHeaderModel(
                props.filterValues,
                props.ecd,
                props.fedd
              )
            : null,
        },
        { method: "POST", responseType: "blob" }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Re-Schedulement_` + props.filterValues.applicationNum + `.pdf`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setMessage("Download Completed");
        setAlert(true);
      });
  };

  const amortgridColumns = [
    {
      field: "noOfDays",
      headerName: "No. Of Days",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
      type: "string",
      editable: false,
    },
    {
      field: "duePeriod",
      headerName: "Due Period",
      headerAlign: "center",
      align: "center",
      minWidth: 190,
      flex: 1,
      type: "string",
      editable: false,
    },
    {
      field: "openingPrincipal",
      headerName: "Opening Principal (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.openingPrincipal),
    },
    {
      field: "emi",
      headerName: "EMI (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.emi),
    },
    {
      field: "prePayment",
      headerName: "Pre Payments (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.prePayment),
    },
    {
      field: "principalComponent",
      headerName: "Principal (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.principalComponent),
    },
    {
      field: "interestComponent",
      headerName: "Interest (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.interestComponent),
    },
    {
      field: "closingPrincipal",
      headerName: "Closing Principal (₹)",
      headerAlign: "center",
      align: "right",
      minWidth: 160,
      flex: 1,
      type: "string",
      editable: false,
      valueGetter: (params) => amountFormatter(params.row.closingPrincipal),
    },
  ];

  return (
    <>
      <Dialog
        fullScreen
        open={props.openDialog}
        onClose={handleClose}
        TransitionComponent={Transition}
        className={classes.root}
      >
        <Toolbar>
          <Typography
            sx={{
              ml: 2,
              flex: 1,
            }}
          >
            <h4>Revised Repayment Schedule</h4>
          </Typography>
          <Tooltip title="Ctrl+J" placement="top">
            <Button
              autoFocus
              variant="contained"
              sx={{
                marginRight: "8px",
              }}
              disabled={props.amortValues === null}
              onClick={onDownload}
              ref={downloadRef}
            >
              <DownloadRounded />
              Download
            </Button>
          </Tooltip>
          <Tooltip title="Ctrl+Q" placement="top">
            <Button variant="contained" onClick={handleClose} ref={closeRef}>
              Close
            </Button>
          </Tooltip>
        </Toolbar>
        <Box
          sx={{
            marginTop: "8px",
            marginBottom: "8px",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              marginTop: "8px",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CustomDataGrid
              noDataMessage="No Data"
              rows={
                props.amortValues !== null
                  ? props.amortValues.map((item, index) => ({
                      id: index + 1,
                      ...item,
                    }))
                  : []
              }
              columns={amortgridColumns}
              getRow
              pageSize={20}
              pageSizeOptions={[20]}
              gridLazyLoad={() => {}}
              gridHeight={490}
              gridWidth={window.innerWidth - 40}
              onCellKeyDown={(event, param) => {
                (param.key === "e" || param.key === "E") &&
                  param.preventDefault();
              }}
            />
          </Box>
          <Snackbar
            open={alert}
            autoHideDuration={3000}
            anchorOrigin={{ vertical, horizontal }}
            onClose={() => setAlert(false)}
          >
            <Alert
              onClose={() => setAlert(false)}
              severity={alertType}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {message}
            </Alert>
          </Snackbar>
        </Box>
      </Dialog>
    </>
  );
};
export default RevisedRepaymentDialog;
